/** True for an IANA zone id this browser can format with. Anything else (null, empty, a typo) is treated as "not set". */
export function isValidTimeZone(id: string | null | undefined): id is string {
  if (!id) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `formatDateOnly('2026-10-31') === 'Oct 31, 2026'`. A plain calendar date, so it is never shifted by a time zone. */
export function formatDateOnly(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

/** `formatPeriodRange('2026-10-01', '2026-10-31') === 'Oct 1 – Oct 31, 2026'` (the year is shown once when both ends share it). */
export function formatPeriodRange(startIso: string, endIso: string): string {
  const [sy, sm, sd] = startIso.split('-').map(Number);
  const [ey, em, ed] = endIso.split('-').map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return `${startIso} – ${endIso}`;
  return sy === ey
    ? `${MONTHS[sm - 1]} ${sd} – ${MONTHS[em - 1]} ${ed}, ${ey}`
    : `${MONTHS[sm - 1]} ${sd}, ${sy} – ${MONTHS[em - 1]} ${ed}, ${ey}`;
}

/** The moment the figures were generated, written in the company's own time zone with its abbreviation (for example "Oct 31, 2026, 2:14 PM PDT"). */
export function formatInstant(iso: string, timeZoneId: string): string {
  const zone = isValidTimeZone(timeZoneId) ? timeZoneId : 'UTC';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(date);
}

/** A friendly name for a zone, such as "Pacific Time". Falls back to the id itself. */
export function timeZoneName(timeZoneId: string): string {
  if (!isValidTimeZone(timeZoneId)) return timeZoneId;
  const part = new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId, timeZoneName: 'longGeneric' })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? timeZoneId;
}

const US_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Puerto_Rico', 'Pacific/Guam',
];

/** Zones offered when choosing a company time zone: the United States first, then every other zone this browser knows. */
export function listTimeZones(): { us: string[]; other: string[] } {
  let all: string[] = [];
  try {
    all = Intl.supportedValuesOf('timeZone');
  } catch {
    all = [];
  }
  const us = US_ZONES.filter(isValidTimeZone);
  return { us, other: all.filter((z) => !us.includes(z)) };
}
